import {
  useCustomMutation,
  useInvalidate,
  useCan,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Modal } from 'antd';

const { confirm } = Modal;

interface UseUserActionsProps {
  userId?: string | number;
}

interface UserAction {
  mutate: () => void;
  isLoading: boolean;
  canExecute: boolean;
}

interface UserActions {
  block: UserAction;
  unblock: UserAction;
  verify: UserAction;
  resetPassword: UserAction;
}

/**
 * Custom hook for user management actions
 * Provides reusable user actions with permissions, mutations, and notifications
 * Can be used in both list and show views
 */
export const useUserActions = ({
  userId,
}: UseUserActionsProps): UserActions => {
  const t = useTranslate();
  const invalidate = useInvalidate();
  const { open } = useNotification();

  // Permission checks
  const { data: canBlock } = useCan({
    resource: 'iam.users',
    action: 'block',
    params: { id: userId },
  });

  const { data: canUnblock } = useCan({
    resource: 'iam.users',
    action: 'unblock',
    params: { id: userId },
  });

  const { data: canVerify } = useCan({
    resource: 'iam.users',
    action: 'verify',
    params: { id: userId },
  });

  const { data: canResetPassword } = useCan({
    resource: 'iam.users',
    action: 'reset_password',
    params: { id: userId },
  });

  // Mutations
  const {
    mutate: blockUser,
    mutation: { isPending: isBlocking },
  } = useCustomMutation();
  const {
    mutate: unblockUser,
    mutation: { isPending: isUnblocking },
  } = useCustomMutation();
  const {
    mutate: verifyUser,
    mutation: { isPending: isVerifying },
  } = useCustomMutation();
  const {
    mutate: resetPassword,
    mutation: { isPending: isResetting },
  } = useCustomMutation();

  // Action handlers
  const handleBlock = () => {
    if (!userId) return;

    confirm({
      title: t('iam.users.actions.block_confirm'),
      onOk: () => {
        blockUser(
          {
            url: `iam/users/${userId}/block`,
            method: 'patch',
            values: {},
          },
          {
            onSuccess: () => {
              invalidate({
                resource: 'iam.users',
                invalidates: ['detail', 'list'],
                id: userId,
              });
              open?.({
                type: 'success',
                message: t('notifications.success'),
                description: t('iam.users.actions.block_success'),
              });
            },
          }
        );
      },
    });
  };

  const handleUnblock = () => {
    if (!userId) return;

    unblockUser(
      {
        url: `iam/users/${userId}/unblock`,
        method: 'patch',
        values: {},
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'iam.users',
            invalidates: ['detail', 'list'],
            id: userId,
          });
          open?.({
            type: 'success',
            message: t('notifications.success'),
            description: t('iam.users.actions.unblock_success'),
          });
        },
      }
    );
  };

  const handleVerify = () => {
    if (!userId) return;

    verifyUser(
      {
        url: `iam/users/${userId}/verify`,
        method: 'patch',
        values: {},
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'iam.users',
            invalidates: ['detail', 'list'],
            id: userId,
          });
          open?.({
            type: 'success',
            message: t('notifications.success'),
            description: t('iam.users.actions.verify_success'),
          });
        },
      }
    );
  };

  const handleResetPassword = () => {
    if (!userId) return;

    confirm({
      title: t('iam.users.actions.reset_password_confirm'),
      onOk: () => {
        resetPassword(
          {
            url: `iam/users/${userId}/forgot_password`,
            method: 'post',
            values: {},
          },
          {
            onSuccess: () => {
              open?.({
                type: 'success',
                message: t('notifications.success'),
                description: t('iam.users.actions.reset_password_success'),
              });
            },
          }
        );
      },
    });
  };

  return {
    block: {
      mutate: handleBlock,
      isLoading: isBlocking,
      canExecute: canBlock?.can ?? false,
    },
    unblock: {
      mutate: handleUnblock,
      isLoading: isUnblocking,
      canExecute: canUnblock?.can ?? false,
    },
    verify: {
      mutate: handleVerify,
      isLoading: isVerifying,
      canExecute: canVerify?.can ?? false,
    },
    resetPassword: {
      mutate: handleResetPassword,
      isLoading: isResetting,
      canExecute: canResetPassword?.can ?? false,
    },
  };
};
